/**
 * Shared validation utilities for address fields.
 * Used in both client profile Account page and Autofill question types.
 */

import { AutofillSource } from '../../generated/graphql';

/**
 * Validates date of birth format and reasonableness.
 * Ensures the date is in the past and represents a reasonable age (0-120 years).
 *
 * @param value - The date value to validate (ISO date string)
 * @returns true if valid or empty, error message string if invalid
 */
export const validateDateOfBirth = (
  value: string | undefined,
): true | string => {
  if (!value) return true;

  const date = new Date(value);
  const now = new Date();
  const age = now.getFullYear() - date.getFullYear();

  // Check if date is valid
  if (isNaN(date.getTime())) {
    return 'Invalid date format';
  }

  // Check if date is in the past and age is reasonable (0-120 years)
  if (date >= now || age < 0 || age > 120) {
    return 'Date of birth must be in the past and reasonable';
  }

  return true;
};

/**
 * Validates postal code format.
 * Accepts US ZIP codes (5 digits, optional +4) or international postal codes (3-10 alphanumeric characters).
 *
 * @param value - The postal code value to validate
 * @returns true if valid or empty, error message string if invalid
 */

export const validatePostalCode = (
  value: string | undefined,
): true | string => {
  if (!value) return true;

  const usZipRegex = /^\d{5}(-\d{4})?$/;
  const intlPostalRegex = /^[A-Za-z0-9\s-]{3,10}$/;

  if (usZipRegex.test(value) || intlPostalRegex.test(value)) {
    return true;
  }

  return 'Invalid postal code format';
};

/**
 * Gets validation rules for an autofill field based on its source type.
 * Returns appropriate validation rules matching the client profile Account page.
 *
 * @param autofillSource - The autofill source type
 * @param isRequired - Whether the field is required
 * @returns React Hook Form validation rules
 */
export const getAutofillFieldValidation = (
  autofillSource: AutofillSource,
  isRequired: boolean,
) => {
  const baseRules: {
    required?: boolean | { value: boolean; message: string };
  } = {};

  if (isRequired) {
    baseRules.required = {
      value: true,
      message: 'This field is required',
    };
  }

  // Add postal code validation if the source is AddressPostalCode
  // Matches the validation used in Account page
  if (autofillSource === AutofillSource.AddressPostalCode) {
    return {
      ...baseRules,
      validate: validatePostalCode,
    };
  }

  // Add date of birth validation if the source is DateOfBirth
  // Matches the validation used in Account page
  if (autofillSource === AutofillSource.DateOfBirth) {
    return {
      ...baseRules,
      validate: validateDateOfBirth,
    };
  }

  // For other address fields (street, city, state, country), no additional validation
  // They use the same simple registration as in Account page
  return baseRules;
};
